import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, ChevronDown, ChevronRight, Home, Building2, Wrench, Users, MessageSquare, Sparkles, Crown, MapPin, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo-badge-tagline.png";

interface NavItem { name: string; href: string; isDisabled?: boolean; isHighlighted?: boolean; }

const residentialServices: NavItem[] = [
  { name: "Tile & Grout Cleaning", href: "/tile-grout-cleaning" },
  { name: "Permanent Grout Restoration", href: "/services/tile-grout-restoration" },
  { name: "Stone Floor Grinding & Polishing", href: "/services/stone-floor" },
  { name: "Tile & Stone Repair", href: "/tile-stone-repair", isHighlighted: true },
  { name: "Countertop Restoration", href: "/services/countertop" },
  { name: "Shower Restoration", href: "/services/shower-restoration" },
  { name: "Natural Stone Shower Restoration", href: "/natural-stone-shower-restoration" },
  { name: "Slip Resistant Coatings", href: "/services/slip-resistant" },
  { name: "Epoxy Installation", href: "/services/epoxy" },
  { name: "Patio & Pool Deck", href: "/services/patio-pool-deck" },
  { name: "Headstone Restoration", href: "/headstone-restoration" },
  { name: "─────────────────", href: "#", isDisabled: true },
  { name: "Travertine Restoration", href: "/travertine-restoration" },
  { name: "Marble Polishing", href: "/marble-polishing" },
  { name: "Stone Sealing", href: "/stone-sealing" },
];

const commercialServices: NavItem[] = [
  { name: "Tile & Grout Cleaning", href: "/commercial/tile-grout-cleaning" },
  { name: "Tile & Grout Restoration", href: "/commercial/tile-grout-restoration" },
  { name: "Stone Floor Grinding & Polishing", href: "/commercial/stone-floor" },
  { name: "Countertop Restoration", href: "/commercial/countertop" },
  { name: "Shower Restoration", href: "/commercial/shower-restoration" },
  { name: "Slip Resistant Coatings", href: "/commercial/slip-resistant" },
  { name: "Epoxy Installation", href: "/commercial/epoxy" },
  { name: "Patio & Pool Deck", href: "/commercial/patio-pool-deck" },
  { name: "─────────────────", href: "#", isDisabled: true },
  { name: "Cemetery & Mausoleum Care", href: "/commercial/cemetery-mausoleum-restoration" },
  { name: "Pool Deck Restoration", href: "/pool-deck-restoration" },
  { name: "Saltillo Tile Restoration", href: "/saltillo-tile-restoration" },
];

const serviceAreas = [
  { name: "Trilogy at Power Ranch, Gilbert", href: "/service-areas/trilogy-at-power-ranch-gilbert-az" },
  { name: "Las Sendas, Mesa", href: "/service-areas/las-sendas-mesa-az" },
  { name: "The Pecans, Queen Creek", href: "/service-areas/the-pecans-queen-creek-az" },
  { name: "Chandler (Home Base)", href: "/service-areas#chandler" },
  { name: "Gilbert", href: "/gilbert-tile-grout-restoration" },
  { name: "Mesa", href: "/service-areas#mesa" },
  { name: "Queen Creek & San Tan Valley", href: "/service-areas#queen-creek" },
  { name: "Scottsdale", href: "/service-areas#scottsdale" },
  { name: "Paradise Valley", href: "/service-areas#paradise-valley" },
  { name: "Ahwatukee", href: "/service-areas#ahwatukee" },
  { name: "Tempe", href: "/service-areas#tempe" },
  { name: "─────────────────", href: "#", isDisabled: true },
  { name: "View All Service Areas", href: "/service-areas", isHighlighted: true },
];

const navItems = [
  { name: "Home", href: "/" },
  { name: "Residential", href: "/services", dropdownType: "residential" },
  { name: "Commercial", href: "/commercial", dropdownType: "commercial" },
  { name: "Service Areas", href: "/service-areas", dropdownType: "serviceAreas" },
  { name: "About", href: "/about", dropdownType: "about" },
];

const aboutPages = [
  { name: "About Us", href: "/about" },
  { name: "The Zone", href: "/the-zone" },
  { name: "Price Calculator", href: "/price-calculator", isHighlighted: true },
  { name: "Grout Quiz (60 sec)", href: "/grout-quiz", isHighlighted: true },
  { name: "Photo Assessment", href: "/free-assessment", isHighlighted: true },
  { name: "Builders & Realtors", href: "/builder-realtor" },
  { name: "Testimonials", href: "/testimonials" },
  { name: "FAQ", href: "/faq" },
  { name: "Blog", href: "/blog" },
  { name: "Promotions", href: "/promotions" },
  { name: "─────────────────", href: "#", isDisabled: true },
  { name: "Contact Us", href: "/contact", isHighlighted: true },
];

// Quick access tabs for mobile - these are just the main navigation tabs
const mobileQuickTabs = [
  { name: "Home", href: "/", icon: Home, hasDropdown: false },
  { name: "Residential", href: "/services", icon: Wrench, hasDropdown: true, dropdownType: "residential" as const },
  { name: "Commercial", href: "/commercial", icon: Building2, hasDropdown: true, dropdownType: "commercial" as const },
  { name: "Areas", href: "/service-areas", icon: MapPin, hasDropdown: true, dropdownType: "serviceAreas" as const },
  { name: "Info", href: "/about", icon: Users, hasDropdown: true, dropdownType: "about" as const },
];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [residentialOpen, setResidentialOpen] = useState(false);
  const [commercialOpen, setCommercialOpen] = useState(false);
  const [serviceAreasOpen, setServiceAreasOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileResidentialOpen, setMobileResidentialOpen] = useState(false);
  const [mobileCommercialOpen, setMobileCommercialOpen] = useState(false);
  const [mobileServiceAreasOpen, setMobileServiceAreasOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setMobileResidentialOpen(false);
    setMobileCommercialOpen(false);
    setMobileServiceAreasOpen(false);
    setMobileAboutOpen(false);
  }, [location.pathname]);

  const getDropdownState = (type: string | undefined) => {
    if (type === "residential") return residentialOpen;
    if (type === "commercial") return commercialOpen;
    if (type === "serviceAreas") return serviceAreasOpen;
    if (type === "about") return aboutOpen;
    return false;
  };

  const setDropdownState = (type: string | undefined, value: boolean) => {
    if (type === "residential") setResidentialOpen(value);
    if (type === "commercial") setCommercialOpen(value);
    if (type === "serviceAreas") setServiceAreasOpen(value);
    if (type === "about") setAboutOpen(value);
  };

  const getServices = (type: string | undefined) => {
    if (type === "residential") return residentialServices;
    if (type === "commercial") return commercialServices;
    if (type === "serviceAreas") return serviceAreas;
    if (type === "about") return aboutPages;
    return [];
  };

  const getDropdownLabel = (type: string | undefined) => {
    if (type === "residential") return "Residential Services";
    if (type === "commercial") return "Commercial Services";
    if (type === "serviceAreas") return "Service Areas";
    if (type === "about") return "About & Info";
    return "";
  };

  const menuVariants = {
    closed: {
      opacity: 0,
      x: "100%",
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 40
      }
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 40,
        staggerChildren: 0.07,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    closed: { opacity: 0, x: 50, y: 0 },
    open: { opacity: 1, x: 0, y: 0 }
  };

  return (
    <>
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
    >
      Skip to main content
    </a>
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-background lg:bg-background/80 lg:backdrop-blur-sm ${
        scrolled 
          ? "lg:bg-background/98 lg:backdrop-blur-md border-b border-primary/10 shadow-lg" 
          : ""
      }`}
    >
      <nav className="container-custom" aria-label="Primary">

        <div className="flex items-center justify-between h-[70px] sm:h-20">
          {/* Mobile: Empty spacer for balance | Desktop: Navigation */}
          <div className="lg:hidden w-11 sm:w-12" />
          
          {/* Logo - Centered on mobile */}
          <Link to="/" className="flex items-center shrink-0">
            <img 
              src={logo} 
              alt="Lazona Tile Care - Cleaning, Restoration, Repairs" 
              className="h-[54px] sm:h-[62px] md:h-[66px] lg:h-[74px] w-auto object-contain"
              width={117}
              height={74}
            />


          </Link>



          {/* Desktop Navigation - Close to logo, no gap */}
          <div className="hidden lg:flex items-center gap-0 ml-4">
            {navItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdownType && setDropdownState(item.dropdownType, true)}
                onMouseLeave={() => item.dropdownType && setDropdownState(item.dropdownType, false)}
              >
                <Link
                  to={item.href}
                  className={`px-4 py-2 text-sm font-semibold tracking-wide uppercase transition-all duration-300 flex items-center gap-1.5 whitespace-nowrap ${
                    location.pathname === item.href || 
                    (item.dropdownType === "residential" && location.pathname.startsWith("/services")) ||
                    (item.dropdownType === "commercial" && location.pathname.startsWith("/commercial"))
                      ? "text-primary"
                      : "text-foreground/70 hover:text-primary"
                  }`}
                >
                  {item.name}
                  {item.dropdownType && <ChevronDown className="w-3 h-3" />}
                </Link>

                {/* Dropdown */}
                {item.dropdownType && (
                  <AnimatePresence>
                    {getDropdownState(item.dropdownType) && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-72 border border-primary/20 rounded-lg shadow-2xl z-[100] flex flex-col"
                        style={{ backgroundColor: 'hsl(220 20% 14%)' }}
                      >
                        <div className="py-2 px-3 border-b border-primary/10 flex-shrink-0" style={{ backgroundColor: 'hsl(220 20% 18%)' }}>
                          <span className="text-xs font-bold uppercase tracking-wider text-primary">
                            {getDropdownLabel(item.dropdownType)}
                          </span>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto">
                          {getServices(item.dropdownType).map((service: { name: string; href: string; isDisabled?: boolean; isHighlighted?: boolean }) => (
                            service.isDisabled ? (
                              <div key={service.name} className="px-5 py-1 text-xs text-foreground/30 select-none">
                                {service.name}
                              </div>
                            ) : (
                              <Link
                                key={`${service.name}-${service.href}`}
                                to={service.href}
                                className={`block px-5 py-3 text-sm border-b border-border/30 last:border-b-0 transition-all duration-200 ${
                                  service.isHighlighted 
                                    ? "text-primary font-semibold hover:bg-primary/10" 
                                    : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                                }`}
                              >
                                {service.name}
                              </Link>
                            )
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons - Closer to nav */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Shiny Explore Our Blog Button */}
            <Link to="/blog" className="relative group">
              <div
                className="relative flex items-center gap-2 px-4 py-2 bg-background rounded-lg border border-accent/50 text-sm font-semibold text-accent group-hover:text-primary group-hover:border-primary/50 transition-colors font-display tracking-tight"
              >
                <BookOpen className="w-4 h-4" />
                <span>Explore Our Blog</span>
                <Sparkles className="w-3 h-3 opacity-70" />
              </div>
            </Link>
            
            <Button variant="gold" size="sm" asChild>
              <a href="tel:4803520392">
                <Phone className="w-3 h-3" />
                CALL NOW
              </a>
            </Button>
          </div>

          {/* Mobile/Tablet Call Button */}
          <div className="lg:hidden">
            <Button variant="gold" size="sm" asChild>
              <a href="tel:4803520392" className="flex items-center gap-1" aria-label="Call Now (480) 352-0392">
                <Phone className="w-3 h-3" />
                <span className="hidden sm:inline">CALL</span>
              </a>
            </Button>
          </div>
        </div>

        {/* Mobile Quick Tabs - Always Visible on Mobile with Dropdown Support */}
        <div className="lg:hidden pb-2 sm:pb-3">
          <div className="flex items-center justify-between gap-0.5 sm:gap-1 p-0.5 sm:p-1 bg-secondary/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-primary/10">
            {mobileQuickTabs.map((tab) => {
              const isActive = location.pathname === tab.href || 
                (tab.href === "/services" && location.pathname.startsWith("/services")) ||
                (tab.href === "/commercial" && location.pathname.startsWith("/commercial")) ||
                (tab.href === "/service-areas" && serviceAreas.some(a => location.pathname === a.href)) ||
                (tab.href === "/about" && aboutPages.some(a => location.pathname === a.href));
              const isDropdownOpen = activeTab === tab.name;
              
              return (
                <div key={tab.href} className="flex-1 relative">
                  <button
                    onClick={() => {
                      if (tab.hasDropdown) {
                        setActiveTab(isDropdownOpen ? null : tab.name);
                      } else {
                        window.location.href = tab.href;
                      }
                    }}
                    className={`w-full flex flex-col items-center gap-0.5 sm:gap-1 py-1.5 sm:py-2 px-1 sm:px-2 rounded-lg sm:rounded-xl transition-all duration-300 ${
                      isActive 
                        ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" 
                        : isDropdownOpen
                          ? "bg-accent/20 text-accent"
                          : "text-muted-foreground hover:text-primary hover:bg-primary/10"
                    }`}
                  >
                    <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide sm:tracking-wider whitespace-nowrap flex items-center gap-0.5">
                      {tab.name}
                      {tab.hasDropdown && <ChevronDown className={`w-2 h-2 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
          
          {/* Mobile Tab Dropdowns */}
          <AnimatePresence>
            {activeTab && (
              <motion.div
                initial={{ opacity: 0, y: -10, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -10, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-2 rounded-xl border border-primary/20 overflow-hidden"
                style={{ backgroundColor: 'hsl(220 20% 14%)' }}
              >
                <div className="max-h-[50vh] overflow-y-auto">
                  {activeTab === "Residential" && (
                    <>
                      <Link
                        to="/services"
                        onClick={() => setActiveTab(null)}
                        className="block px-4 py-3 text-sm font-semibold text-accent border-b border-primary/10 hover:bg-primary/10"
                      >
                        View All Residential Services →
                      </Link>
                      {residentialServices.filter(s => !s.isDisabled).map((service) => (
                        <Link
                          key={`${service.name}-${service.href}`}
                          to={service.href}
                          onClick={() => setActiveTab(null)}
                          className={`block px-4 py-2.5 text-sm border-b border-border/20 last:border-b-0 transition-colors ${
                            service.isHighlighted 
                              ? "text-accent font-semibold hover:bg-accent/10" 
                              : "text-foreground/80 hover:text-primary hover:bg-primary/5"
                          }`}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </>
                  )}
                  {activeTab === "Commercial" && (
                    <>
                      <Link
                        to="/commercial"
                        onClick={() => setActiveTab(null)}
                        className="block px-4 py-3 text-sm font-semibold text-accent border-b border-primary/10 hover:bg-primary/10"
                      >
                        View All Commercial Services →
                      </Link>
                      {commercialServices.filter(s => !s.isDisabled).map((service) => (
                        <Link
                          key={`${service.name}-${service.href}`}
                          to={service.href}
                          onClick={() => setActiveTab(null)}
                          className={`block px-4 py-2.5 text-sm border-b border-border/20 last:border-b-0 transition-colors ${
                            service.isHighlighted 
                              ? "text-accent font-semibold hover:bg-accent/10" 
                              : "text-foreground/80 hover:text-primary hover:bg-primary/5"
                          }`}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </>
                  )}
                  {activeTab === "Areas" && (
                    <>
                      <Link
                        to="/service-areas"
                        onClick={() => setActiveTab(null)}
                        className="block px-4 py-3 text-sm font-semibold text-accent border-b border-primary/10 hover:bg-primary/10"
                      >
                        View All Service Areas →
                      </Link>
                      {serviceAreas.filter(s => !s.isDisabled).map((area) => (
                        <Link
                          key={area.href}
                          to={area.href}
                          onClick={() => setActiveTab(null)}
                          className={`block px-4 py-2.5 text-sm border-b border-border/20 last:border-b-0 transition-colors ${
                            area.isHighlighted 
                              ? "text-accent font-semibold hover:bg-accent/10" 
                              : "text-foreground/80 hover:text-primary hover:bg-primary/5"
                          }`}
                        >
                          {area.name}
                        </Link>
                      ))}
                    </>
                  )}
                  {activeTab === "Info" && (
                    <>
                      {aboutPages.filter(s => !s.isDisabled).map((page) => (
                        <Link
                          key={page.href}
                          to={page.href}
                          onClick={() => setActiveTab(null)}
                          className={`block px-4 py-2.5 text-sm border-b border-border/20 last:border-b-0 transition-colors ${
                            page.isHighlighted 
                              ? "text-accent font-semibold hover:bg-accent/10" 
                              : "text-foreground/80 hover:text-primary hover:bg-primary/5"
                          }`}
                        >
                          {page.name}
                        </Link>
                      ))}
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Gold accent line under header */}
        <div 
          className="h-0.5 w-full"
          style={{
            background: 'linear-gradient(90deg, transparent, hsl(40 70% 50% / 0.6), hsl(42 80% 60% / 0.8), hsl(40 70% 50% / 0.6), transparent)'
          }}
        />
      </nav>

      {/* Mobile Full-Screen Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 top-20 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Slide-in Menu Panel */}
            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="lg:hidden fixed top-20 right-0 bottom-0 w-[85%] max-w-md border-l border-primary/20 z-50 overflow-hidden"
              style={{ backgroundColor: 'hsl(220 20% 12%)' }}
            >
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none" />

              <div className="relative h-full flex flex-col">
                {/* Menu Header */}
                <motion.div 
                  variants={itemVariants}
                  className="px-6 py-5 border-b border-primary/10"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Menu</p>
                      <p className="text-sm text-muted-foreground">Navigate Our Services</p>
                    </div>
                  </div>
                </motion.div>

                {/* Scrollable Menu Content */}
                <div className="flex-1 overflow-y-auto py-4 px-4 space-y-2">
                  {/* Home */}
                  <motion.div variants={itemVariants}>
                    <Link
                      to="/"
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 group ${
                        location.pathname === "/" 
                          ? "bg-primary/15 border border-primary/30" 
                          : "hover:bg-primary/10 border border-transparent"
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        location.pathname === "/" 
                          ? "bg-primary text-primary-foreground" 
                          : "bg-muted group-hover:bg-primary/20"
                      }`}>
                        <Home className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-foreground">Home</span>
                        <p className="text-xs text-muted-foreground">Back to main page</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </Link>
                  </motion.div>

                  {/* Residential Services Accordion */}
                  <motion.div variants={itemVariants}>
                    <button
                      onClick={() => setMobileResidentialOpen(!mobileResidentialOpen)}
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 group ${
                        mobileResidentialOpen || location.pathname.startsWith("/services")
                          ? "bg-primary/15 border border-primary/30" 
                          : "hover:bg-primary/10 border border-transparent"
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        location.pathname.startsWith("/services")
                          ? "bg-primary text-primary-foreground" 
                          : "bg-muted group-hover:bg-primary/20"
                      }`}>
                        <Wrench className="w-5 h-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <span className="font-semibold text-foreground">Residential</span>
                        <p className="text-xs text-muted-foreground">Home restoration services</p>
                      </div>
                      <motion.div
                        animate={{ rotate: mobileResidentialOpen ? 90 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {mobileResidentialOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-6 mt-2 pl-4 border-l-2 border-primary/30 space-y-1">
                            {residentialServices.filter(s => !s.isDisabled).map((service, idx) => (
                              <motion.div
                                key={`${service.name}-${service.href}`}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.03 }}
                              >
                                <Link
                                  to={service.href}
                                  onClick={() => setIsOpen(false)}
                                  className={`block py-2.5 px-3 text-sm rounded-lg transition-all duration-200 ${
                                    service.isHighlighted 
                                      ? "text-accent font-semibold hover:bg-accent/10" 
                                      : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                                  }`}
                                >
                                  {service.name}
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {/* Commercial Services Accordion */}
                  <motion.div variants={itemVariants}>
                    <button
                      onClick={() => setMobileCommercialOpen(!mobileCommercialOpen)}
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 group ${
                        mobileCommercialOpen || location.pathname.startsWith("/commercial")
                          ? "bg-accent/15 border border-accent/30" 
                          : "hover:bg-accent/10 border border-transparent"
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        location.pathname.startsWith("/commercial")
                          ? "bg-accent text-white" 
                          : "bg-muted group-hover:bg-accent/20"
                      }`}>
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <span className="font-semibold text-foreground">Commercial</span>
                        <p className="text-xs text-muted-foreground">Business & enterprise</p>
                      </div>
                      <motion.div
                        animate={{ rotate: mobileCommercialOpen ? 90 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {mobileCommercialOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-6 mt-2 pl-4 border-l-2 border-accent/30 space-y-1">
                            {commercialServices.filter(s => !s.isDisabled).map((service, idx) => (
                              <motion.div
                                key={`${service.name}-${service.href}`}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.03 }}
                              >
                                <Link
                                  to={service.href}
                                  onClick={() => setIsOpen(false)}
                                  className={`block py-2.5 px-3 text-sm rounded-lg transition-all duration-200 ${
                                    service.isHighlighted 
                                      ? "text-accent font-semibold hover:bg-accent/10" 
                                      : "text-foreground/70 hover:text-accent hover:bg-accent/5"
                                  }`}
                                >
                                  {service.name}
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {/* Service Areas Accordion */}
                  <motion.div variants={itemVariants}>
                    <button
                      onClick={() => setMobileServiceAreasOpen(!mobileServiceAreasOpen)}
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 group ${
                        mobileServiceAreasOpen || location.pathname === "/service-areas" || ["/paradise-valley-az", "/gilbert-az", "/chandler-az", "/mesa-az", "/ahwatukee-az"].includes(location.pathname)
                          ? "bg-primary/15 border border-primary/30" 
                          : "hover:bg-primary/10 border border-transparent"
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        ["/paradise-valley-az", "/gilbert-az", "/chandler-az", "/mesa-az", "/ahwatukee-az", "/service-areas"].includes(location.pathname)
                          ? "bg-primary text-primary-foreground" 
                          : "bg-muted group-hover:bg-primary/20"
                      }`}>
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <span className="font-semibold text-foreground">Service Areas</span>
                        <p className="text-xs text-muted-foreground">Cities we serve</p>
                      </div>
                      <motion.div
                        animate={{ rotate: mobileServiceAreasOpen ? 90 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {mobileServiceAreasOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-6 mt-2 pl-4 border-l-2 border-primary/30 space-y-1">
                            {serviceAreas.filter(s => !s.isDisabled).map((area, idx) => (
                              <motion.div
                                key={area.href}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.03 }}
                              >
                                <Link
                                  to={area.href}
                                  onClick={() => setIsOpen(false)}
                                  className={`block py-2.5 px-3 text-sm rounded-lg transition-all duration-200 ${
                                    area.isHighlighted 
                                      ? "text-primary font-semibold hover:bg-primary/10" 
                                      : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                                  }`}
                                >
                                  {area.name}
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {/* About & Info Accordion */}
                  <motion.div variants={itemVariants}>
                    <button
                      onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 group ${
                        mobileAboutOpen || ["/about", "/site-prep", "/the-zone", "/faq", "/blog", "/promotions", "/contact", "/testimonials"].includes(location.pathname)
                          ? "bg-primary/15 border border-primary/30" 
                          : "hover:bg-primary/10 border border-transparent"
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        ["/about", "/site-prep", "/the-zone", "/faq", "/blog", "/promotions", "/contact", "/testimonials"].includes(location.pathname)
                          ? "bg-primary text-primary-foreground" 
                          : "bg-muted group-hover:bg-primary/20"
                      }`}>
                        <Users className="w-5 h-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <span className="font-semibold text-foreground">About & Info</span>
                        <p className="text-xs text-muted-foreground">Company, FAQ, Blog & more</p>
                      </div>
                      <motion.div
                        animate={{ rotate: mobileAboutOpen ? 90 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {mobileAboutOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-6 mt-2 pl-4 border-l-2 border-primary/30 space-y-1">
                            {aboutPages.filter(s => !s.isDisabled).map((page, idx) => (
                              <motion.div
                                key={page.href}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.03 }}
                              >
                                <Link
                                  to={page.href}
                                  onClick={() => setIsOpen(false)}
                                  className={`block py-2.5 px-3 text-sm rounded-lg transition-all duration-200 ${
                                    page.isHighlighted 
                                      ? "text-primary font-semibold hover:bg-primary/10" 
                                      : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                                  }`}
                                >
                                  {page.name}
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>

                {/* Bottom CTA */}
                <motion.div 
                  variants={itemVariants}
                  className="px-6 py-5 border-t border-primary/10 bg-gradient-to-t from-secondary to-transparent"
                >
                  <Button variant="gold" size="lg" className="w-full shadow-xl shadow-accent/20" asChild>
                    <a href="tel:4803520392" className="flex items-center justify-center gap-3">
                      <Phone className="w-5 h-5" />
                      <span>Request Consultation</span>
                    </a>
                  </Button>
                  <p className="text-center text-xs text-muted-foreground mt-3">
                    (480) 352-0392 • Call Now
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
    </>
  );
}

